import { overflow } from "@prismicio/editor-support/String"
import {
  Button,
  Card,
  CardHeader,
  CardHeaderActions,
  CardHeaderTitle,
  Text
} from "@prismicio/editor-ui"
import { type ReactNode, useState } from "react"

import type { CropperImage } from "../../../core/imageUtils"
import type { MediaAssetOrExternalImage } from "../../../core/MediaLibrary/hooks/mediaLibraryData"
import { type CroppedImages, CropperDialog } from "../CropperDialog"
import { MediaDialog } from "../MediaDialog"

interface ImageFieldCardProps {
  url: string | undefined
  selected?: boolean
  fieldLabel: string
  readOnly: boolean
  imageLabel: string[]
  onMediaSelected: (media: MediaAssetOrExternalImage) => void
  initialImages: CropperImage[]
  initialOption?: number
  onCropperDialogClose: (result: {
    croppedImages: CroppedImages
    option: number
  }) => void
  onClear: () => void
  children: ReactNode
}

export function ImageFieldCard(props: ImageFieldCardProps) {
  const {
    url,
    selected,
    fieldLabel,
    readOnly,
    imageLabel,
    onMediaSelected,
    initialImages,
    initialOption = 0,
    onCropperDialogClose,
    onClear,
    children
  } = props

  const [isOpen, setIsOpen] = useState(false)

  const isSVGContentView = url?.split("?")[0]?.slice(-4) === ".svg"

  const visibility = url && !readOnly ? "visible" : "hidden"

  return (
    <Card
      color={selected ? "indigo2" : "grey2"}
      variant={selected ? "selected" : "dashed"}
      radius={4}
    >
      <CardHeader sx={{ flexWrap: "wrap" }}>
        <CardHeaderTitle>
          <Text noWrap>{overflow(fieldLabel, 30)}</Text>
          {imageLabel.length !== 0 && (
            <Text color="grey11" noSelect>
              {` • ${imageLabel.join(" • ")}`}
            </Text>
          )}
        </CardHeaderTitle>
        <CardHeaderActions sx={{ visibility }}>
          <CropperDialog
            trigger={
              <Button startIcon="crop" disabled={isSVGContentView}>
                Crop & Resize
              </Button>
            }
            initialImages={initialImages}
            initialOption={initialOption}
            onClose={onCropperDialogClose}
          />
          <Button startIcon="imageSearch" onClick={() => setIsOpen(true)}>
            Replace
          </Button>
          <Button onClick={onClear} startIcon="delete">
            Clear
          </Button>
        </CardHeaderActions>
      </CardHeader>
      {children}

      <MediaDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        onMediaSelected={onMediaSelected}
        assetType="image"
      />
    </Card>
  )
}
